import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useEffect, useMemo } from "react";
import { memoForwardRef } from "@shared/client/util/memo";
import { ScrollView } from "@shared/client/components/scroll-view/scroll-view";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { detectLinkPlatform } from "@/config/platform";
import { shouldRenderShippingFromLink } from "@/config/shipping-link";
import { getBlockClasses, getBlockStyleVars, persistPostitColor, removeLegacyStickyStyle, } from "@/config/block-styles";
import { LinkBlockView } from "@shared/blocks/link/view";
import { getLinkTitle, getLinkDescription, getLinkSiteName } from "@shared/blocks/link/helpers";
import { CloudDocumentLinkBlockView } from "@shared/blocks/link/cloud-document/view";
import { TweetBlockView } from "@shared/blocks/tweet/view";
import { YouTubeBlockView } from "@shared/blocks/youtube/view";
import { WikipediaBlockView } from "@shared/blocks/wikipedia/view";
import { DictionaryBlockView } from "@shared/blocks/dictionary/view";
import { SubstackBlockView } from "@shared/blocks/link/substack/view";
import { GitHubBlockView } from "@shared/blocks/link/github/view";
import { GistBlockView } from "@shared/blocks/link/gist/view";
import { AudioBlockView } from "@shared/blocks/audio/view";
import { ImageBlockView } from "@shared/blocks/image/view";
import { VideoBlockView } from "@shared/blocks/video/view";
import { DocumentBlockView } from "@shared/blocks/document/view";
import { TextBlockView } from "@shared/blocks/text/view";
import { EmailBlockView } from "@shared/blocks/email/view";
import { NoteBlockContent } from "./content/note-block-content";
import { ShippingLinkBlockContent } from "./content/shipping-link-block-content";
import "./block-view.scss";
function resolveLinkView(block) {
    const linkContent = block.content;
    // TODO(shipping-staging-fallback): keep URL-platform check until staging
    // reliably enriches UPS/FedEx links with content.shipping at ingest time.
    // TODO(shipping-refresh): Once API refresh-on-read guarantees hydrated shipping
    // payloads, route shipping view by `linkContent.shipping` only.
    if (shouldRenderShippingFromLink(linkContent)) {
        return ShippingLinkBlockContent;
    }
    const platform = detectLinkPlatform(linkContent);
    switch (platform) {
        case "twitter":
            return TweetBlockView;
        case "youtube":
            return YouTubeBlockView;
        case "wikipedia":
            return WikipediaBlockView;
        case "dictionary":
            return DictionaryBlockView;
        case "substack":
            return SubstackBlockView;
        case "gist":
            return GistBlockView;
        case "github":
            return GitHubBlockView;
        case "notion":
        case "google_docs":
            return CloudDocumentLinkBlockView;
        default:
            return LinkBlockView;
    }
}
function LinkBlockContent({ block, blockParams }) {
    if (block.type !== "link")
        return null;
    const LinkView = resolveLinkView(block);
    return _jsx(LinkView, { block: block, blockParams: blockParams });
}
const contentRegistry = new Map();
contentRegistry.set("link", LinkBlockContent);
contentRegistry.set("audio", AudioBlockView);
contentRegistry.set("image", ImageBlockView);
contentRegistry.set("video", VideoBlockView);
contentRegistry.set("text", TextBlockView);
contentRegistry.set("note", NoteBlockContent);
contentRegistry.set("document", DocumentBlockView);
contentRegistry.set("email", EmailBlockView);
export function resolveContentComponent(block) {
    return contentRegistry.get(block.type);
}
export function UnsupportedBlockContent(_props) {
    return _jsx("span", { className: "card-error-label", children: "UNSUPPORTED" });
}
// Short human-readable date for the corner chip. Returns e.g. "May 23".
// Used on non-chronological surfaces (search results, smart collections,
// the Pinned section) where the surrounding layout doesn't already label
// each card's date — the chip carries the "when was this captured?"
// signal at a glance. Falls back to undefined for missing/invalid input
// so the chip is omitted rather than rendering a broken label.
function formatChipDate(iso) {
    if (!iso)
        return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return undefined;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
// Compact time-of-day for the corner chip. Returns e.g. "9:43a", "12:05p".
// Used on chronological surfaces (day-grouped Stream) where the group
// header already labels the date, so the per-card chip narrows down to
// the time within the day. Lowercase a/p (rather than uppercase AM/PM
// or a 24h clock) keeps the chip narrow enough not to crowd the
// bottom-right corner of small cards.
function formatChipTime(iso) {
    if (!iso)
        return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return undefined;
    const hours24 = d.getHours();
    const minutes = d.getMinutes();
    const isPm = hours24 >= 12;
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12}:${String(minutes).padStart(2, "0")}${isPm ? "p" : "a"}`;
}
// Compact relative-day label for the edit-history tooltip — "today" /
// "yesterday" for the near-past (the common case for stream cards),
// falling back to "MMM d" for older blocks. Paired with `formatChipTime`
// in `formatChipHistoryLine` below so each line of the tooltip reads as
// e.g. "10:26p · today" without dragging in the full weekday-and-year
// formatter used by the native `title` tooltip (which would crowd the
// dark hover popover with redundant date info on a same-day edit).
function formatRelativeDay(iso) {
    if (!iso)
        return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return undefined;
    const now = new Date();
    const sameDay = d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    if (sameDay)
        return "today";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.getFullYear() === yesterday.getFullYear() &&
        d.getMonth() === yesterday.getMonth() &&
        d.getDate() === yesterday.getDate();
    if (isYesterday)
        return "yesterday";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
// Joins time + relative day into a single tooltip line value
// (e.g. "10:26p · today"). Returns undefined when the input is missing
// so callers can omit the whole line rather than rendering "—".
function formatChipHistoryLine(iso) {
    const time = formatChipTime(iso);
    const day = formatRelativeDay(iso);
    if (!time || !day)
        return undefined;
    return `${time} \u00B7 ${day}`;
}
// Edit-detection threshold. Updates that land within this window of
// creation are treated as a continuation of the ingest write (server
// upserts that touch `updatedAt` during processing, the user
// double-tapping save on a freshly-captured block) and don't trip the
// pencil indicator — matches the design rec's "Trivial edits: typo
// fixes within ~2 minutes of creation don't trip the dot" rule, so
// the indicator carries weight when it does appear.
const MEANINGFUL_EDIT_THRESHOLD_MS = 2 * 60 * 1000;
/**
 * True when the block carries a non-trivial edit — i.e. `updatedAt` is
 * present and lands more than `MEANINGFUL_EDIT_THRESHOLD_MS` after
 * `createdAt`. Drives the pencil indicator on the corner chip and the
 * "edited" line in the hover tooltip.
 */
function isBlockEdited(createdAt, updatedAt) {
    if (!updatedAt)
        return false;
    const created = new Date(createdAt).getTime();
    const updated = new Date(updatedAt).getTime();
    if (Number.isNaN(created) || Number.isNaN(updated))
        return false;
    return updated - created > MEANINGFUL_EDIT_THRESHOLD_MS;
}
export function isEmptyBlock(block) {
    const c = block.content;
    if (!c || (typeof c === "object" && Object.keys(c).length === 0))
        return true;
    switch (block.type) {
        case "text":
            return !c.text && !block.title;
        case "image":
            return !c.file;
        case "audio":
            return !c.file;
        case "video":
            return !c.file;
        case "link": {
            // A link block is "empty" only if it has no displayable content at all.
            // While ingestion is in flight the URL may not be populated yet, but the
            // envelope often already carries a title / siteName / description / block
            // title — those are still useful preview text and should render normally
            // instead of falling back to the dashed ERROR shell.
            const link = c;
            const gistFilesCount = link.subtype === "gist" ? link.gist.files.length : 0;
            return (!link.url &&
                !getLinkTitle(link) &&
                !getLinkSiteName(link) &&
                !getLinkDescription(link) &&
                gistFilesCount === 0 &&
                !block.title &&
                !block.summary);
        }
        case "document":
            return !c.file;
        default:
            return false;
    }
}
const DateChipDisplay = memo(function DateChipDisplay({ blockId, createdAt, updatedAt, surfaceAt, dateChipMode, }) {
    // The chip is suppressed on the error / empty shells (no content to
    // anchor a timestamp to) and on `dateChipMode === "none"` (detail
    // modal, showcase) — handled by the parent before this component is
    // rendered. In the two visible modes, the chip's primary label reads
    // from `surfaceAt` — the same key the stream uses to sort and
    // day-group, so the per-card chip stays in lockstep with the
    // surrounding layout (a re-surfaced block reads as "now" both in the
    // chip and in its position in the feed).
    //
    // When `surfaceAt` differs from `createdAt` (block bumped via
    // reminder / manual surface), the secondary "surfaced" line in the
    // hover tooltip below carries the original creation time so the user
    // can reach both timestamps from a single chip. The chip itself
    // stays a single token — matches the design rec's "corner stays a
    // single token, so the grid keeps its rhythm" rule rather than
    // hanging a permanent secondary label off the pill. Comparing the
    // *formatted* strings (rather than raw ISO timestamps) suppresses
    // sub-minute ingestion-lag deltas — freshly captured blocks read as
    // "surfaceAt === createdAt" and the surfaced row doesn't render.
    const formatChip = dateChipMode === "time" ? formatChipTime : formatChipDate;
    const chipLabel = formatChip(surfaceAt);
    if (!chipLabel)
        return null;
    const surfaceChipLabel = formatChip(createdAt);
    const hasSurfaceDelta = surfaceChipLabel !== undefined && chipLabel !== surfaceChipLabel;
    // Edit-aware chip surface. The pencil icon and the dark hover
    // tooltip are gated on `isEdited` rather than the re-surface delta —
    // they're a separate signal layered over the existing chip chrome:
    //
    //   - `isEdited`: paint a small pencil glyph inside the chip pill
    //     so the user sees at a glance that this block has been edited
    //     since capture. Threshold-gated so ingest-time writes that
    //     stamp `updatedAt` a few seconds after `createdAt` don't trip
    //     a permanent "edited" marker on every block.
    //   - Hover tooltip: always renders on the chip (regardless of
    //     edit/re-surface state) so the user can hover any timestamp
    //     to read the exact times — created, edited (if edited), and
    //     surfaced (if re-surfaced). Replaces the native `title`
    //     attribute, which couldn't paint multi-line role-labelled
    //     output and would conflict with the custom popover by
    //     popping a yellow OS tooltip on the same hover.
    const isEdited = isBlockEdited(createdAt, updatedAt);
    const createdHistory = formatChipHistoryLine(createdAt);
    const editedHistory = isEdited ? formatChipHistoryLine(updatedAt) : undefined;
    const surfacedHistory = hasSurfaceDelta ? formatChipHistoryLine(surfaceAt) : undefined;
    const chipTooltipId = `block-view__date-chip-tooltip-${blockId}`;
    return (_jsxs("span", { className: "block-view__date-chip", "aria-describedby": createdHistory ? chipTooltipId : undefined, children: [_jsx("span", { className: "block-view__date-chip-label", children: chipLabel }), isEdited ? (_jsx(FaIcon, { icon: faPencil, className: "block-view__date-chip-edited-icon", "aria-label": "Edited" })) : null] }));
});
export const BlockView = memoForwardRef(({ envelope, ContentComponent, blockParams, isSelected, isLassoHovered, isDragGhost, zIndex, toolbar, onClick, gestureBind, treatEmptyAsPending, dateChipMode = "date", creatorAvatar, replierAvatars, }, ref) => {
    const block = envelope.block;
    const linkContent = block.type === "link" ? block.content : undefined;
    const rendersShippingView = linkContent ? shouldRenderShippingFromLink(linkContent) : false;
    const isEmpty = useMemo(() => isEmptyBlock(block), [block]);
    const showErrorShell = isEmpty && treatEmptyAsPending !== true;
    const blockModifierClasses = useMemo(() => getBlockClasses(block), [block]);
    const blockStyleVars = useMemo(() => getBlockStyleVars(block), [block]);
    const noPaddingShell = !isEmpty && (block.type === "image" || block.type === "video" || rendersShippingView);
    const isShortText = block.type === "text" && (block.content.text?.length ?? 0) < 150;
    useEffect(() => {
        if (isShortText) {
            removeLegacyStickyStyle(block.id);
            persistPostitColor();
        }
    }, [block.id, isShortText]);
    // Memoized so the `.filter().join()` walk doesn't churn on every
    // chrome-only re-render (selection toggle, lasso hover, etc. only
    // flip one input at a time, but they all live in the same string).
    const classNames = useMemo(() => [
        "block-view",
        isDragGhost && "block-view--ghost",
        isSelected && "block-view--selected",
        isLassoHovered && "block-view--lasso-hover",
        noPaddingShell && "block-view--no-padding",
        showErrorShell && "block-view--error",
        ...blockModifierClasses,
    ]
        .filter(Boolean)
        .join(" "), [
        isDragGhost,
        isSelected,
        isLassoHovered,
        noPaddingShell,
        showErrorShell,
        blockModifierClasses,
    ]);
    // `body` is memoized so the `<ContentComponent ... />` element keeps a
    // stable reference across chrome-only re-renders. Without this, the
    // `<ScrollView>` wrapper below sees a new `children` prop on every
    // render and its `memo` short-circuit fails — re-running the heavy
    // `OverlayScrollbarsComponent` underneath whenever the user toggles
    // selection or hovers a lasso.
    const body = useMemo(() => {
        if (showErrorShell) {
            return _jsx("span", { className: "card-error-label", children: "ERROR" });
        }
        if (isEmpty) {
            return null;
        }
        return _jsx(ContentComponent, { block: block, blockParams: blockParams });
    }, [showErrorShell, isEmpty, ContentComponent, block, blockParams]);
    // In the expanded detail modal, long-text card types (text/note/document
    // — the three that render through the global `.card-content` element)
    // should scroll their body inside the panel instead of clipping at a
    // fixed height with a bottom-fade mask. The mask is the right
    // affordance for a fixed-size stream tile, but actively hides the
    // content the user explicitly opened the panel to read. Image / audio
    // / video have their own internal layouts that already fill the panel,
    // so the wrap is scoped to card-content consumers only.
    //
    // The matching `.card-content` overrides (drop mask, grow height to
    // content) live in `block-detail-modal.scss` under
    // `.block-detail-modal--expanded` so the close transition snaps the
    // content back to its in-stream dimensions in lockstep with the panel
    // shrinking.
    const usesCardContent = block.type === "text" || block.type === "note" || block.type === "document";
    const wrapInScroll = blockParams?.isExpanded === true && usesCardContent && !showErrorShell && !isEmpty;
    const renderedBody = useMemo(() => {
        if (wrapInScroll) {
            // `trackOffset={-12}` floats the overlay scrollbar 12px past the
            // card's right content edge so the thumb sits in the panel's
            // padding gutter rather than flush against the body text. The
            // card keeps its `--modal-card-padding` on the right, so only
            // the bar moves outward — the text column stays put. `.block-view`
            // has `overflow: hidden`, so the bar is clipped to the panel's
            // rounded edge without escaping the modal.
            return (_jsx(ScrollView, { className: "block-view__scroll", axis: "y", trackOffset: -12, children: body }));
        }
        return body;
    }, [wrapInScroll, body]);
    // The chip's formatting + tooltip subtree live behind the
    // `DateChipDisplay` memo boundary — see its docblock above. We only
    // pay the cost of constructing the element wrapper here when the
    // chip is actually visible (i.e. not on the error / empty shells
    // or in `dateChipMode === "none"`).
    const dateChip = dateChipMode !== "none" && !showErrorShell && !isEmpty ? (_jsx(DateChipDisplay, { blockId: block.id, createdAt: block.createdAt, updatedAt: block.updatedAt, surfaceAt: block.surfaceAt, dateChipMode: dateChipMode })) : null;
    // Single positioned wrapper for the top-right meta row (date chip
    // and creator avatar). The chip + creator avatar are both
    // `--corner-meta-h` tall so visually they read as a centered pair.
    // The replier stack is rendered as a SEPARATE absolutely-positioned
    // sibling below (`.block-view__replier-stack`) — keeping it out of
    // this flex row dodges the bottom-anchor's "row grows upward when
    // content is taller than the chip" trap that an in-row stacked
    // column would hit.
    const cornerMeta = dateChip || creatorAvatar ? (_jsxs("div", { className: "block-view__corner-meta", children: [dateChip, creatorAvatar] })) : null;
    // Replier avatars live in their own absolutely-positioned column
    // anchored just beneath the corner-meta on the right side of the
    // card. Independent positioning means the column grows DOWNWARD
    // into the card body without competing with the bottom-anchored
    // corner-meta's transition (which has to interpolate `bottom`
    // smoothly into the modal-expanded state). Only renders when both
    // a creator avatar AND replier avatars were provided — without
    // the creator above them, the small chips would float without
    // anchoring context.
    const replierStack = creatorAvatar && replierAvatars ? (_jsx("div", { className: "block-view__replier-stack", "aria-hidden": true, children: replierAvatars })) : null;
    return (_jsxs("div", { ref: ref, className: classNames, "data-block-id": block.id, ...(gestureBind?.() ?? {}), onClick: onClick, style: {
            ...blockStyleVars,
            ...(zIndex ? { zIndex } : {}),
        }, children: [renderedBody, cornerMeta, replierStack, toolbar] }));
});
